import React, { useState, useEffect } from 'react';
import { ArrowLeft, Eye, Target, Heart, Users, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getText, getCurrentLanguage } from '../../services/dataSync';

const MissionVisionPage = () => {
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="max-w-5xl mx-auto">
            {/* About Us Button - Top */}
            <div className="mb-4">
              <Link to="/about" className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-800 hover:text-black border-2 border-primary-600 hover:border-primary-700 rounded-lg transition-all duration-300 group shadow-md hover:shadow-lg">
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium text-sm">{getText('nav.about.submenu.about')}</span>
              </Link>
            </div>
            
            <div className="text-center space-y-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight tracking-tight">
                {getText('nav.about.submenu.mission')}
              </h1>
              <p className="text-lg md:text-xl text-blue-100 font-medium">
                {getText('about.mission.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="section py-16 bg-white">
        <div className="container-narrow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Vision */}
            <div className="group">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 md:p-10 text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-2 h-full flex flex-col">
                <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl w-24 h-24 flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform icon-ring">
                  <Eye className="h-12 w-12 text-white icon-strong" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 group-hover:text-blue-600 transition-colors">
                  {getText('about.vision.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed text-base md:text-lg font-medium flex-grow">
                  {getText('about.vision.desc')}
                </p>
              </div>
            </div>

            {/* Mission */}
            <div className="group">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-8 md:p-10 text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-2 h-full flex flex-col">
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl w-24 h-24 flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform icon-ring">
                  <Target className="h-12 w-12 text-white icon-strong" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 group-hover:text-emerald-600 transition-colors">
                  {getText('about.mission.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed text-base md:text-lg font-medium flex-grow">
                  {getText('about.mission.desc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Objectives */}
      <section className="section py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="container-medium">
          <div className="section-header mb-16 text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {getText('about.objectives.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-primary-600 mx-auto mb-6 rounded-full"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {getText('about.objectives.desc')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Sustainable Growth */}
            <div className="group relative">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-3 border border-gray-100 h-full flex flex-col relative overflow-hidden">
                {/* Decorative Background */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-transparent rounded-full blur-2xl opacity-50 group-hover:opacity-70 transition-opacity"></div>
                
                {/* Icon Container */}
                <div className="relative z-10 mb-6">
                  <div className="bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-2xl w-20 h-20 flex items-center justify-center mx-auto shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 ring-4 ring-blue-100">
                    <Heart className="h-10 w-10 text-white" />
                  </div>
                </div>
                
                {/* Content */}
                <div className="relative z-10 flex-grow flex flex-col">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors text-center">
                    {getText('about.objectives.sustainable.title')}
                  </h3>
                  <div className="w-16 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto mb-4 rounded-full"></div>
                  <p className="text-gray-600 leading-relaxed text-center flex-grow">
                    {getText('about.objectives.sustainable.desc')}
                  </p>
                </div>
                
                {/* Bottom Accent */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 rounded-b-2xl"></div>
              </div>
            </div>

            {/* Industry Collaboration */}
            <div className="group relative">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-3 border border-gray-100 h-full flex flex-col relative overflow-hidden">
                {/* Decorative Background */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-100 to-transparent rounded-full blur-2xl opacity-50 group-hover:opacity-70 transition-opacity"></div>
                
                {/* Icon Container */}
                <div className="relative z-10 mb-6">
                  <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 rounded-2xl w-20 h-20 flex items-center justify-center mx-auto shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 ring-4 ring-emerald-100">
                    <Users className="h-10 w-10 text-white" />
                  </div>
                </div>
                
                {/* Content */}
                <div className="relative z-10 flex-grow flex flex-col">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-emerald-600 transition-colors text-center">
                    {getText('about.objectives.collaboration.title')}
                  </h3>
                  <div className="w-16 h-0.5 bg-gradient-to-r from-emerald-500 to-emerald-600 mx-auto mb-4 rounded-full"></div>
                  <p className="text-gray-600 leading-relaxed text-center flex-grow">
                    {getText('about.objectives.collaboration.desc')}
                  </p>
                </div>
                
                {/* Bottom Accent */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-emerald-600 to-emerald-500 rounded-b-2xl"></div>
              </div>
            </div>

            {/* Innovation Support */}
            <div className="group relative">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-3 border border-gray-100 h-full flex flex-col relative overflow-hidden">
                {/* Decorative Background */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-100 to-transparent rounded-full blur-2xl opacity-50 group-hover:opacity-70 transition-opacity"></div>
                
                {/* Icon Container */}
                <div className="relative z-10 mb-6">
                  <div className="bg-gradient-to-br from-purple-500 via-purple-600 to-purple-700 rounded-2xl w-20 h-20 flex items-center justify-center mx-auto shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 ring-4 ring-purple-100">
                    <TrendingUp className="h-10 w-10 text-white" />
                  </div>
                </div>
                
                {/* Content */}
                <div className="relative z-10 flex-grow flex flex-col">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-purple-600 transition-colors text-center">
                    {getText('about.objectives.innovation.title')}
                  </h3>
                  <div className="w-16 h-0.5 bg-gradient-to-r from-purple-500 to-purple-600 mx-auto mb-4 rounded-full"></div>
                  <p className="text-gray-600 leading-relaxed text-center flex-grow">
                    {getText('about.objectives.innovation.desc')}
                  </p>
                </div>
                
                {/* Bottom Accent */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-purple-600 to-purple-500 rounded-b-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default MissionVisionPage;
